/*
 * Trade first, then a person (AB#1624): the rules the work order form follows when it hands an
 * order to a trade (yrkesgrupp), kept pure so they can be tested without rendering a dialog.
 *
 * Shared with the escalate dialog and the schedule form, which pick a trade the same way.
 *
 * Everything here works off getTrades(companyKey, true): each trade arrives with its members and
 * its departments, so the form needs no second read to route a department or to list who is in
 * a trade.
 */
import { isCmmsTechnician } from "../cmmsSettings/tradeMemberOptions";

const DEPARTMENT_NODE_TYPE = 2;

const titleOf = (id, name, username) => name || username || `#${id}`;

const byTitle = (a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" });

/**
 * The active trades that get a department's work orders, by the server's rule: walk up from the
 * department, itself included, and stop at the first department with an active trade mapped. A
 * sub-department's own mapping therefore overrides its parent's.
 *
 * The same walk as TradeService.GetDepartmentRoutingAsync, down to its two limits: only
 * department nodes are climbed, so the walk ends at the first ancestor that is something else,
 * and a visited set guards against a cycle in old data. The one difference is the tree: the form
 * loads it without inactive nodes, so an inactive department in the middle of the chain ends the
 * walk here where the server would climb through it. That only costs a pre-fill — the server
 * still derives the trade on create.
 *
 * flatNodes is flattenAssetNodes(tree). Answers trade ids in ascending order, as the server does,
 * and an empty list when nothing routes.
 */
export function getEffectiveTradeIds(departmentId, trades, flatNodes) {
  if (departmentId == null) return [];

  const tradeIdsByDepartment = new Map();
  for (const trade of trades ?? []) {
    if (!trade.isActive) continue;
    for (const department of trade.departments ?? []) {
      const ids = tradeIdsByDepartment.get(department.assetNodeId) ?? new Set();
      ids.add(trade.id);
      tradeIdsByDepartment.set(department.assetNodeId, ids);
    }
  }

  const nodeById = new Map((flatNodes ?? []).map((node) => [node.id, node]));
  const visited = new Set();
  let node = nodeById.get(departmentId);

  while (node && node.nodeType === DEPARTMENT_NODE_TYPE && !visited.has(node.id)) {
    visited.add(node.id);
    const ids = tradeIdsByDepartment.get(node.id);
    if (ids) return [...ids].sort((a, b) => a - b);
    node = nodeById.get(node.parentAssetNodeId);
  }

  return [];
}

export const findTrade = (trades, tradeId) =>
  (tradeId == null ? null : (trades ?? []).find((trade) => trade.id === tradeId) ?? null);

export const isTradeMember = (trade, userId) =>
  (trade?.members ?? []).some((member) => member.userId === userId);

/**
 * The trade to pre-fill when the department changes, or null to leave the field alone.
 *
 * Only when exactly one trade routes there — with two the choice is the manager's, and the server
 * would leave it open as well. And only when the person already named is a member of it or nobody
 * is named: the server lets a named person win over a derived trade, and pre-filling one they are
 * not in would make the next save a 400 for something the manager never chose.
 *
 * Never clears. A trade picked by hand that does not serve the new department is still a choice,
 * and an explicit choice wins.
 */
export function getPrefillTradeId(departmentId, assignedResourceId, trades, flatNodes) {
  const routed = getEffectiveTradeIds(departmentId, trades, flatNodes);
  if (routed.length !== 1) return null;

  const trade = findTrade(trades, routed[0]);
  if (assignedResourceId != null && !isTradeMember(trade, assignedResourceId)) return null;

  return routed[0];
}

/**
 * Options for a trade picker: the active trades, plus the current one if it has been retired.
 *
 * currentId is the live form value. A retired trade cannot be given new work — the server answers
 * CMMS_TRADE_INACTIVE — so it is not offered. But an order already on one keeps it through an
 * edit that leaves it alone, and without it in the list the field would render empty while the id
 * rode along in form state and was saved back unseen.
 *
 * Each option carries isActive. formatRetired names the retired one, so the reader can tell why it
 * is there.
 */
export function toTradeOptions(trades, currentId, formatRetired = (name) => name) {
  return (trades ?? [])
    .filter((trade) => trade.isActive || trade.id === currentId)
    .map((trade) => ({
      id: trade.id,
      title: trade.isActive ? trade.name : formatRetired(trade.name),
      isActive: trade.isActive,
    }));
}

/**
 * Options for the list page's trade filter: every trade, the retired ones after the active.
 * Orders keep a retired trade, so the filter must still be able to find them.
 */
export function toTradeFilterOptions(trades, formatRetired = (name) => name) {
  const all = trades ?? [];
  return [...all.filter((trade) => trade.isActive), ...all.filter((trade) => !trade.isActive)]
    .map((trade) => ({
      id: trade.id,
      title: trade.isActive ? trade.name : formatRetired(trade.name),
      isActive: trade.isActive,
    }));
}

/**
 * Options for the "assigned to" picker: with a trade, its members; without one, every technician.
 *
 * Only the technician tier either way (Technician + Admin), which is AB#1619's rule — the one
 * cmms/cmmsAssigneeOptions carries on master. The server checks membership, not the role, so a
 * member who has lost the role could be saved; they are left out because they cannot claim or
 * complete the job.
 *
 * currentId is the form's live assignee and is kept in the list whatever it is, for the reason
 * toTradeOptions keeps a retired trade: someone named before the trade was set, or before they
 * left it, has to be on screen to be taken off. users names them when the trade does not.
 */
export function toTradeAssigneeOptions(users, trade, currentId) {
  if (!trade) {
    return (users ?? [])
      .filter((user) => isCmmsTechnician(user) || (currentId != null && user.id === currentId))
      .map((user) => ({ id: user.id, title: titleOf(user.id, user.name, user.username) }));
  }

  const options = (trade.members ?? [])
    .filter((member) => member.isTechnician || member.userId === currentId)
    .map((member) => ({ id: member.userId, title: titleOf(member.userId, member.name, member.username) }));

  if (currentId != null && !options.some((option) => option.id === currentId)) {
    const user = (users ?? []).find((candidate) => candidate.id === currentId);
    options.push({ id: currentId, title: user ? titleOf(user.id, user.name, user.username) : `#${currentId}` });
  }

  return options.sort(byTitle);
}
